import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Star } from 'lucide-react';
import { ratingsService, PendingRating } from '@/services/ratings';
import { toast } from 'sonner';

export interface RatingModalProps {
  isOpen: boolean;
  appointment: PendingRating | null;
  onSuccess: () => void;
}

export function RatingModal({ isOpen, appointment, onSuccess }: RatingModalProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!appointment) return null;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Por favor, selecione uma nota de 1 a 5 estrelas.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await ratingsService.submitRating({
        appointmentId: appointment.id,
        rating,
        comment,
      });

      if (response.success) {
        toast.success('Avaliação enviada com sucesso!');
        setRating(0);
        setComment('');
        onSuccess();
      } else {
        toast.error(response.error || 'Erro ao enviar avaliação.');
      }
    } catch (error) {
      toast.error('Erro de conexão ao enviar avaliação.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayName = appointment.doctor 
    ? `Dr(a). ${appointment.doctor.user.name}` 
    : appointment.patient?.name || 'Paciente';

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-[425px]" 
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">Avaliação Mandatória</DialogTitle>
          <DialogDescription className="text-center">
            Sua consulta com <strong>{displayName}</strong> do dia {new Date(appointment.appointmentDate).toLocaleDateString()} foi finalizada. 
            Avalie para continuar.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          <div className="flex flex-col items-center gap-4">
            <Label className="text-center font-medium text-lg">Como foi sua experiência?</Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="focus:outline-none transform transition-transform active:scale-90"
                  onClick={() => setRating(star)}
                >
                  <Star
                    className={`w-10 h-10 transition-colors ${
                      star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="comment" className="font-medium">Comentário (opcional):</Label>
            <Textarea
              id="comment"
              placeholder="Conte-nos mais sobre o atendimento..."
              className="min-h-[100px] resize-none"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button 
            className="w-full h-12 text-lg font-semibold bg-primary hover:bg-primary/90" 
            onClick={handleSubmit} 
            disabled={isSubmitting || rating === 0}
          >
            {isSubmitting ? 'Enviando...' : 'Finalizar e Desbloquear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
